import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2, Car, FileText, PartyPopper } from "lucide-react";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";

const euro = (n) => `${Number(n).toFixed(2)} €`;

export default function QuotePage() {
  const { token } = useParams();
  const { t } = useLang();
  const [data, setData] = useState(undefined); // undefined=loading, null=not found
  const [acting, setActing] = useState(false);

  const fetchQuote = () => {
    api.get(`/quote/${token}`).then(({ data }) => setData(data)).catch(() => setData(null));
  };
  useEffect(fetchQuote, [token]);

  const respond = async (action) => {
    setActing(true);
    try {
      const { data: res } = await api.post(`/quote/${token}/respond`, { action });
      setData((d) => ({ ...d, status: res.status }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setActing(false); }
  };

  const Shell = ({ children }) => (
    <div className="noise-overlay bg-[#050505] min-h-screen grid-bg">
      <div className="max-w-2xl mx-auto px-5 py-10">
        <div className="flex items-center justify-between mb-10">
          <Logo height="h-10" />
          <LanguageSwitcher />
        </div>
        {children}
      </div>
    </div>
  );

  if (data === undefined) {
    return <Shell><div className="flex items-center justify-center py-32 text-zinc-500"><Loader2 className="w-6 h-6 animate-spin" /></div></Shell>;
  }
  if (data === null) {
    return <Shell><div className="border border-white/10 bg-[#0C0C0E] py-20 text-center"><p className="text-white font-display font-700 text-xl">{t("qp.not_found")}</p><Link to="/" className="inline-block mt-6 text-[#00E5FF] font-mono-tech text-sm">{t("qp.home")}</Link></div></Shell>;
  }

  if (data.status === "confirmed" || data.status === "declined") {
    const ok = data.status === "confirmed";
    return (
      <Shell>
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="border border-white/10 bg-[#0C0C0E] p-10 text-center" data-testid="quote-result">
          <div className={`inline-flex w-20 h-20 items-center justify-center border mb-6 ${ok ? "border-[#00E5FF]/40 bg-[#00E5FF]/5" : "border-[#FF1A1A]/40 bg-[#FF1A1A]/5"}`}>
            {ok ? <PartyPopper className="w-9 h-9 text-[#00E5FF]" /> : <XCircle className="w-9 h-9 text-[#FF1A1A]" />}
          </div>
          <h1 className="font-display font-900 text-white text-3xl tracking-tighter uppercase">{ok ? t("qp.accepted_title") : t("qp.declined_title")}</h1>
          <p className="text-zinc-400 mt-4">{ok ? t("qp.accepted_desc") : t("qp.declined_desc")}</p>
          <Link to="/" className="inline-block mt-8 px-7 py-3 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors" data-testid="quote-home-btn">{t("qp.home")}</Link>
        </motion.div>
      </Shell>
    );
  }

  const q = data.quote;
  return (
    <Shell>
      <div className="flex items-center gap-2 font-mono-tech text-xs uppercase tracking-[0.25em] text-[#00E5FF] mb-3">
        <FileText className="w-4 h-4" /> {t("qp.title")}
      </div>
      <div className="border border-white/10 bg-[#0C0C0E]" data-testid="quote-page">
        <div className="bg-[#E60000] px-6 py-4"><h1 className="font-display font-900 text-white text-2xl tracking-tight uppercase">Garage Ter Spelt</h1></div>
        <div className="p-6 space-y-2 font-mono-tech text-sm border-b border-white/10">
          <div className="flex justify-between"><span className="text-zinc-600">{t("qp.for")}</span><span className="text-white">{data.name}</span></div>
          <div className="flex justify-between"><span className="text-zinc-600">{t("qp.service")}</span><span className="text-[#00E5FF]">{t(`services.${data.service_id}.label`) || data.service_label}</span></div>
          <div className="flex justify-between"><span className="text-zinc-600">{t("qp.datetime")}</span><span className="text-white">{data.preferred_date} · {data.preferred_time}</span></div>
          {data.vehicle?.marque && <div className="flex justify-between"><span className="text-zinc-600 flex items-center gap-1"><Car className="w-3.5 h-3.5" />{t("qp.vehicle")}</span><span className="text-white">{data.vehicle.marque} {data.vehicle.modele} {data.vehicle.immatriculation ? `· ${data.vehicle.immatriculation}` : ""}</span></div>}
        </div>

        {!q ? (
          <div className="p-10 text-center text-zinc-400" data-testid="quote-pending">{t("qp.pending")}</div>
        ) : (
          <>
            <div className="p-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="font-mono-tech text-[10px] uppercase tracking-[0.15em] text-zinc-600 border-b border-white/10">
                    <th className="text-left py-2">{t("qp.item")}</th>
                    <th className="text-center py-2 w-14">{t("qp.qty")}</th>
                    <th className="text-right py-2 w-20">{t("qp.unit")}</th>
                    <th className="text-right py-2 w-24">{t("qp.line")}</th>
                  </tr>
                </thead>
                <tbody className="font-mono-tech">
                  {q.items.map((it, i) => (
                    <tr key={i} className="border-b border-white/5" data-testid={`quote-line-${i}`}>
                      <td className="py-2.5 text-white">{it.label} <span className="text-[9px] text-zinc-600 uppercase ml-1">{it.category === "main" ? t("qp.cat_main") : t("qp.cat_piece")}</span></td>
                      <td className="py-2.5 text-center text-zinc-400">{it.qty}</td>
                      <td className="py-2.5 text-right text-zinc-400">{euro(it.unit_price)}</td>
                      <td className="py-2.5 text-right text-white">{euro(it.qty * it.unit_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-5 space-y-1.5 font-mono-tech text-sm">
                <div className="flex justify-between text-zinc-400"><span>{t("qp.subtotal")}</span><span>{euro(q.subtotal)}</span></div>
                <div className="flex justify-between text-zinc-400"><span>{t("qp.tva")} ({q.tva_rate}%)</span><span>{euro(q.tva)}</span></div>
                <div className="flex justify-between text-white text-lg font-700 pt-2 border-t border-white/10"><span>{t("qp.total")}</span><span className="text-[#00E5FF]">{euro(q.total)}</span></div>
              </div>
              {q.notes && <p className="mt-4 text-zinc-500 text-xs italic border-l-2 border-[#E60000] pl-3">{q.notes}</p>}
            </div>

            <div className="p-6 border-t border-white/10 flex flex-col sm:flex-row gap-3">
              <button onClick={() => respond("accept")} disabled={acting} data-testid="quote-accept" className="flex-1 inline-flex items-center justify-center gap-2 py-3.5 bg-[#00E5FF] text-black font-display font-700 uppercase tracking-wide hover:bg-[#33eaff] transition-colors disabled:opacity-50">
                {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} {t("qp.accept")}
              </button>
              <button onClick={() => respond("decline")} disabled={acting} data-testid="quote-decline" className="inline-flex items-center justify-center gap-2 py-3.5 px-6 border border-white/20 text-white font-display font-700 uppercase tracking-wide hover:border-[#FF1A1A] hover:text-[#FF1A1A] transition-colors disabled:opacity-50">
                <XCircle className="w-4 h-4" /> {t("qp.decline")}
              </button>
            </div>
          </>
        )}
      </div>
    </Shell>
  );
}
